import React, { useEffect, useState } from "react";
import { Form, FormFeedback, Input, InputGroup, InputGroupText, Label, Spinner } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createSelector } from "reselect";
import { useSnackbar } from "notistack";
import { loginUser } from "../../store/actions";
import valitanApp1 from "../../assets/images/Valitan-app-1.png";
import valitanApp3 from "../../assets/images/Valitan-app-3.png";
import valitanApp4 from "../../assets/images/Valitan-app-4.png";
import valitanApp5 from "../../assets/images/Valitan-app-5.png";
import valitanAppPics2 from "../../assets/images/Valitan-app-pics2.png";

const loginSelector = createSelector(
  (state) => state.login,
  (loginState) => ({
    error: loginState.error,
    loading: loginState.loading,
  })
);

const adventureSlides = [
  {
    image: valitanApp1,
    alt: "Valitan promotional banner for valuers",
  },
  {
    image: valitanApp4,
    alt: "Valitan offline mobile usage banner",
  },
  {
    image: valitanApp3,
    alt: "Valitan reliable and easy to use banner",
  },
  {
    image: valitanApp5,
    alt: "Valitan carry valuation data everywhere banner",
  },
  {
    image: valitanAppPics2,
    alt: "Valitan print-ready reports banner",
  },
];

const LoginForm = ({ onSuccess, onClose, submitLabel = "Login" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { error, loading } = useSelector(loginSelector);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please enter your username"),
      password: Yup.string().required("Please enter your password"),
    }),
    onSubmit: (values) => {
      if (rememberMe) {
        localStorage.setItem("rememberedUsername", values.username);
        localStorage.setItem("rememberedPassword", values.password);
      } else {
        localStorage.removeItem("rememberedUsername");
        localStorage.removeItem("rememberedPassword");
      }

      const handleSuccess = () => {
        if (onSuccess) {
          onSuccess();
          return;
        }

        navigate("/dashboard");
      };

      dispatch(loginUser(values, handleSuccess));
    },
  });

  useEffect(() => {
    const rememberedUsername = localStorage.getItem("rememberedUsername");
    const rememberedPassword = localStorage.getItem("rememberedPassword");

    if (rememberedUsername && rememberedPassword) {
      setRememberMe(true);
      validation.setValues({
        username: rememberedUsername,
        password: rememberedPassword,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
  }, [enqueueSnackbar, error]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % adventureSlides.length);
    }, 4500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="login-card-shell">
      <div className="login-card">
        <div className="login-left">
          {adventureSlides.map((slide, index) => (
            <div
              key={slide.image}
              className={`login-left-slide ${index === activeSlide ? "is-active" : ""}`}
              style={{ backgroundImage: `url(${slide.image})` }}
              aria-label={slide.alt}
            />
          ))}
          <div className="login-left-content">
            <div className="login-left-dots" aria-hidden="true">
              {adventureSlides.map((slide, index) => (
                <button
                  key={slide.image}
                  type="button"
                  className={index === activeSlide ? "is-active" : ""}
                  onClick={() => setActiveSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="login-right">
          {onClose ? (
            <button type="button" className="login-close" onClick={onClose}>
              ✕
            </button>
          ) : null}

          <h4 className="login-title">Hello! Welcome back</h4>

          <Form
            className="landing-login-form"
            onSubmit={(event) => {
              event.preventDefault();
              validation.handleSubmit();
            }}
          >
            <div className="mb-4">
              <Label className="form-label">Username</Label>
              <Input
                name="username"
                type="text"
                placeholder="Enter username"
                className="landing-login-input"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.username}
                disabled={loading}
                invalid={Boolean(validation.touched.username && validation.errors.username)}
              />
              {validation.touched.username && validation.errors.username ? (
                <FormFeedback type="invalid">{validation.errors.username}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-4">
              <Label className="form-label">Password</Label>
              <InputGroup className="landing-login-input-group">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  className="landing-login-input"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.password}
                  disabled={loading}
                  invalid={Boolean(validation.touched.password && validation.errors.password)}
                />
                <InputGroupText
                  className="landing-login-toggle"
                  style={{ pointerEvents: loading ? "none" : "auto" }}
                  onClick={() => setShowPassword((current) => !current)}
                >
                  <i className={`mdi ${showPassword ? "mdi-eye-off-outline" : "mdi-eye-outline"}`} />
                </InputGroupText>
              </InputGroup>
              {validation.touched.password && validation.errors.password ? (
                <FormFeedback type="invalid" className="d-block">
                  {validation.errors.password}
                </FormFeedback>
              ) : null}
            </div>

            <div className="login-row mb-4">
              <div className="form-check">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="form-check-input"
                  checked={rememberMe}
                  disabled={loading}
                  onChange={(event) => setRememberMe(event.target.checked)}
                />
                <Label for="remember-me" className="form-check-label">
                  Remember me
                </Label>
              </div>

              <Link to="/forgot-password" className="login-link">
                Forgot password?
              </Link>
            </div>

            <button type="submit" className="landing-login-submit w-100" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Signing in...
                </>
              ) : (
                submitLabel
              )}
            </button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
